import React, { useState, useEffect } from 'react';
import RichTable from '../components/richTable/RichTable.jsx';
import Tooltip from '../components/Tooltip.js';
import TwoStepConfirmationButton from '../components/TwoStepConfirmButton.js';
import ActionResult from '../components/ActionResult.js';
import Dropdown from 'react-bootstrap/Dropdown';
import Drawer from '@mui/material/Drawer';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import Badge from 'react-bootstrap/Badge';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';

function MarketDataStatus(props) {

    const [historicalData, setHistoricalData] = useState([]);
    const [quoteData, setQuoteData] = useState({});
    const [quoteHistory, setQuoteHistory] = useState([]);
    const [quoteResult, setQuoteResult] = useState(null);
    const [updatedQuote, setUpdatedQuote] = useState({});
    const [vendorSelected, setVendorSelected] = useState(null);
    const [updates, setUpdates] = useState(0);
    const [quickQuoteSymbol, setQuickQuoteSymbol] = useState(null);
    const [quickQuote, setQuickQuote] = useState(null);
    const [result, setResult] = useState(null);
    const [spyStartDate, setSpyStartDate] = useState(null);

    // Audit State
    const [auditSymbol, setAuditSymbol] = useState(null);
    const [showAuditDrawer, setShowAuditDrawer] = useState(false);
    const [auditLoading, setAuditLoading] = useState(false);
    const [auditResult, setAuditResult] = useState(null);
    const [auditTab, setAuditTab] = useState('missing');
    const [auditNotice, setAuditNotice] = useState(null); // { type: 'success' | 'danger', text: string }

    // Fill In / Add Quote Modal State
    const [showFillModal, setShowFillModal] = useState(false);
    const [fillLoading, setFillLoading] = useState(false);
    const [fillForm, setFillForm] = useState({
        symbol: '',
        date: '',
        close: '',
        open: '',
        high: '',
        low: '',
        volume: ''
    });

    useEffect(() => {
        (async function () {
            let data = await (await fetch(`/api/quoteHistoryAudit`)).json();
            setHistoricalData(data);
            if (Array.isArray(data) && data.length) {
                const spy = data.find(d => d.symbol === 'SPY');
                setSpyStartDate(spy?.startDate || data[0]?.spyStartDate || '2007-1-1');
            }

            let vendor = await (await fetch(`/api/activeQuoteVendor`)).json();
            if (vendor && vendor.vendor) {
                console.log("active quote vendor", vendor.vendor);
                setVendorSelected(vendor.vendor);
            }
        })();
    }, [updates]);

    function getHistoricalData() {
        return historicalData.map(data => {
            return {
                symbol: data.symbol,
                status: data.status || 'pass',
                statusMessage: data.statusMessage || 'Up to date',
                startDate: data.startDate || '-',
                lastDate: data.lastDate || '-',
                count: data.count || 0
            };
        });
    }

    function statusFormatter(cell, row) {
        if (row.status === 'pass') {
            return (
                <span title={row.statusMessage} style={{ cursor: 'help' }}>
                    <Badge bg="success" className="px-2 py-1">
                        ✓ Pass
                    </Badge>
                </span>
            );
        } else if (row.status === 'warning') {
            return (
                <span title={row.statusMessage} style={{ cursor: 'help' }}>
                    <Badge bg="warning" className="text-dark px-2 py-1">
                        ⚠️ Warning
                    </Badge>
                    <span className="ms-1 small text-muted text-nowrap">({row.statusMessage})</span>
                </span>
            );
        } else {
            return (
                <span title={row.statusMessage} style={{ cursor: 'help' }}>
                    <Badge bg="danger" className="px-2 py-1">
                        ✕ Fail
                    </Badge>
                    <span className="ms-1 small text-danger font-monospace text-nowrap">({row.statusMessage})</span>
                </span>
            );
        }
    }

    function dateFormatter(cell) {
        return <span className="font-monospace small text-nowrap">{cell || '-'}</span>;
    }

    async function getQuoteData(symbol) {
        setQuoteData({});
        const url = "/api/quote?symbols=" + symbol;
        const d = await (await fetch(url)).json();
        setQuoteData(d);
    }

    async function getQuoteHistory(symbol) {
        setQuoteHistory([]);
        let date = new Date();
        const url = "/api/quoteHistory?symbol=" + symbol + "&startDate=" + date.toLocaleDateString() + "&endDate=" + date.toLocaleDateString();
        const h = await (await fetch(url)).json();
        setQuoteHistory(h);
    }

    function symbolFormatter(cell, row) {
        return (
            <>
                <Tooltip textClass="tooltip-link" onOpen={() => getQuoteData(row.symbol)} text={cell} json={quoteData} position="right center" width="500px" />
            </>
        );
    }

    async function handleUpdateQuote(symbol) {
        console.log("updatedQuote", updatedQuote);
        const url = "/api/quoteUpdate?symbol=" + symbol + "&close=" + updatedQuote.close;
        const d = await (await fetch(url, { method: 'POST' })).json();
        if (d && d.success) {
            setQuoteResult("Updated Quote");
        } else {
            setQuoteResult("Error updating quote");
        }
        getQuoteHistory(symbol);
    }

    async function handleAddQuote(symbol) {
        const url = "/api/quoteInsertToday?symbol=" + symbol + "&close=" + updatedQuote.close;
        const d = await (await fetch(url, { method: 'POST' })).json();
        if (d && d.success) {
            setQuoteResult("Added Quote");
        } else {
            setQuoteResult("Error updating quote");
        }
        getQuoteHistory(symbol);
        setUpdatedQuote({});
    }

    function onQuoteHistoryOpen(symbol) {
        setQuoteResult(null);
        setUpdatedQuote({});
        getQuoteHistory(symbol);
    }

    function shouldAllowEdits() {
        return props.role >= 3;
    }

    function historicalCountFormatter(cell, row) {
        return (
            <Tooltip textClass="tooltip-link" onOpen={() => onQuoteHistoryOpen(row.symbol)} text={cell} position="right center" width="500px" >
                {quoteHistory && quoteHistory.length ?
                    <>
                        Date: {quoteHistory[0].date}<br></br>
                        Close: <input type="text" autoCorrect="off" defaultValue={quoteHistory[0].close} onChange={(e) => setUpdatedQuote({ ...updatedQuote, close: e.target.value })} /><br></br>
                        Open: {quoteHistory[0].open}<br></br>
                        High: {quoteHistory[0].high}<br></br>
                        Low: {quoteHistory[0].low}<br></br>
                        <button className="btn btn-primary" onClick={(e) => handleUpdateQuote(row.symbol)}>Save</button>
                    </>
                    :
                    <>
                        Close: <input type="text" autoCorrect="off" defaultValue={0} onChange={(e) => setUpdatedQuote({ ...updatedQuote, close: e.target.value })} /><br></br>
                        <button className="btn btn-primary" onClick={(e) => handleAddQuote(row.symbol)}>Add Record</button>
                    </>
                }
                <br></br>
                <>{quoteResult}</>
            </Tooltip>
        );
    }

    async function handleVendorChange(vendor) {
        let url = `/api/quoteVendor?userId=${props.userId}&vendor=${vendor}`;
        let result = await (await fetch(url, { method: 'post' })).json();
        setResult(result);
        setUpdates(updates + 1);
    }

    async function handleUpdateQuoteCache() {
        let url = `/api/updateQuoteCache`;
        await (await fetch(url, { method: 'post' })).json();
        setUpdates(updates + 1);
    }

    async function handleReloadHistoricalData(symbol) {
        setResult({ success: "Started reloading " + symbol });
        let url = `/api/quoteBackfill?symbol=` + symbol;
        let result = await (await fetch(url, { method: 'post' })).json();
        setResult(result);
        setUpdates(updates + 1);
    }

    async function handleLoadQuickQuote() {
        let url = `/api/quoteVendorQuote?symbol=` + quickQuoteSymbol + "&userId=" + props.userId;
        let result = await (await fetch(url)).json();
        if (result.success && result.quote) {
            setQuickQuote(result.quote);
        } else {
            setQuickQuote({ error: result.error });
        }
    }

    // -------------------------------------------------------------
    // AUDIT FUNCTIONS
    // -------------------------------------------------------------
    async function loadAudit(symbol) {
        setAuditLoading(true);
        setAuditNotice(null);
        try {
            const res = await fetch(`/api/quoteHistorySymbolAudit?symbol=${symbol}`);
            const data = await res.json();
            setAuditResult(data);
        } catch (e) {
            console.error("Failed to load quote audit:", e);
            setAuditNotice({ type: 'danger', text: 'Failed to fetch audit data: ' + e.message });
        } finally {
            setAuditLoading(false);
        }
    }

    function normalizeToYMD(raw) {
        if (!raw) return '';
        const str = String(raw).trim();
        const ymd = str.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
        if (ymd) {
            return `${ymd[1]}-${String(ymd[2]).padStart(2, '0')}-${String(ymd[3]).padStart(2, '0')}`;
        }
        const mdy = str.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})/);
        if (mdy) {
            return `${mdy[3]}-${String(mdy[1]).padStart(2, '0')}-${String(mdy[2]).padStart(2, '0')}`;
        }
        const parsed = new Date(str);
        if (!isNaN(parsed.getTime())) {
            const y = parsed.getFullYear();
            const m = String(parsed.getMonth() + 1).padStart(2, '0');
            const d = String(parsed.getDate()).padStart(2, '0');
            if (y >= 1970 && y <= 2099) {
                return `${y}-${m}-${d}`;
            }
        }
        return '';
    }

    function onAuditOpen(symbol) {
        setAuditSymbol(symbol);
        setAuditResult(null);
        setAuditTab('missing');
        setShowAuditDrawer(true);
        loadAudit(symbol);
    }

    async function handleDeleteDuplicate(recordId) {
        if (!recordId) return;
        setAuditNotice(null);
        try {
            const res = await fetch(`/api/quoteDelete?id=${recordId}&symbol=${auditSymbol}`, {
                method: 'DELETE'
            });
            const data = await res.json();
            if (data.success) {
                setAuditNotice({ type: 'success', text: `Deleted record #${recordId}. Refreshing audit...` });
                await loadAudit(auditSymbol);
                setUpdates(prev => prev + 1);
            } else {
                setAuditNotice({ type: 'danger', text: data.error || `Failed to delete record #${recordId}` });
            }
        } catch (e) {
            console.error("Delete duplicate record error:", e);
            setAuditNotice({ type: 'danger', text: 'Error deleting record: ' + e.message });
        }
    }

    function handleOpenFillModal(dateStr = '', targetSymbol = '') {
        const sym = (targetSymbol || auditSymbol || '').trim().toUpperCase();
        const rawDate = String(dateStr || '').trim();
        const formattedDate = normalizeToYMD(rawDate) || rawDate;

        setFillForm({
            symbol: sym,
            date: formattedDate,
            close: '',
            open: '',
            high: '',
            low: '',
            volume: ''
        });
        setShowFillModal(true);
    }

    async function handleSaveFillQuote(e) {
        if (e && e.preventDefault) e.preventDefault();

        const targetSymbol = String(fillForm.symbol || auditSymbol || '').trim().toUpperCase();
        if (!targetSymbol) {
            alert('Symbol is required. Please enter a symbol (e.g. SMH).');
            return;
        }

        const normalizedDate = normalizeToYMD(fillForm.date);
        if (!normalizedDate) {
            alert('A valid Date (YYYY-MM-DD or M/D/YYYY) is required.');
            return;
        }

        const cleanPrice = (val) => {
            if (val === undefined || val === null || val === '') return NaN;
            if (typeof val === 'number') return isNaN(val) ? NaN : val;
            const cleaned = String(val).replace(/[$,]/g, '').trim();
            const num = parseFloat(cleaned);
            return isNaN(num) ? NaN : num;
        };

        const parsedClose = cleanPrice(fillForm.close);
        if (isNaN(parsedClose)) {
            alert('Please enter a valid numeric Close price.');
            return;
        }

        setFillLoading(true);
        try {
            const parsedOpen = cleanPrice(fillForm.open);
            const parsedHigh = cleanPrice(fillForm.high);
            const parsedLow = cleanPrice(fillForm.low);

            const payload = {
                symbol: targetSymbol,
                date: normalizedDate,
                close: parsedClose,
                open: !isNaN(parsedOpen) ? parsedOpen : parsedClose,
                high: !isNaN(parsedHigh) ? parsedHigh : Math.max(parsedClose, !isNaN(parsedOpen) ? parsedOpen : parsedClose),
                low: !isNaN(parsedLow) ? parsedLow : Math.min(parsedClose, !isNaN(parsedOpen) ? parsedOpen : parsedClose),
                volume: parseInt(String(fillForm.volume || '0').replace(/[,]/g, '').trim(), 10) || 0
            };

            const res = await fetch(`/api/quoteInsert`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await res.json();
            if (data.success) {
                setShowFillModal(false);
                setAuditNotice({ type: 'success', text: `Added quote for ${targetSymbol} on ${normalizedDate}. Refreshing audit...` });
                await loadAudit(targetSymbol);
                setUpdates(prev => prev + 1);
            } else {
                alert(data.error || 'Failed to insert quote record');
            }
        } catch (err) {
            console.error("Insert quote error:", err);
            alert('Error adding quote: ' + err.message);
        } finally {
            setFillLoading(false);
        }
    }

    async function handleRemoveSymbol(symbol) {
        if (!symbol) return;
        setResult({ success: `Removing symbol ${symbol}...` });
        try {
            const res = await fetch(`/api/quoteSymbolDelete?symbol=${symbol}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) {
                setResult({ success: data.message || `Removed symbol ${symbol}` });
                setUpdates(prev => prev + 1);
            } else {
                setResult({ error: data.error || `Could not remove symbol ${symbol}` });
            }
        } catch (e) {
            console.error("Remove symbol error:", e);
            setResult({ error: `Error removing symbol: ${e.message}` });
        }
    }

    function reloadDataFormatter(cell, row) {
        return (
            <div className="d-flex align-items-center gap-1 text-nowrap">
                <button className="btn btn-secondary btn-sm" onClick={() => onAuditOpen(cell)}>Audit</button>
                {shouldAllowEdits() ? (
                    <>
                        <TwoStepConfirmationButton
                            id={cell}
                            text={"Reload " + cell}
                            backgroundColor="#0d6efd"
                            onConfirmed={handleReloadHistoricalData}
                        />
                        <TwoStepConfirmationButton
                            id={cell}
                            text={"Remove " + cell}
                            onConfirmed={handleRemoveSymbol}
                        />
                    </>
                ) : null}
            </div>
        );
    }

    return (
        <div>

            {shouldAllowEdits() ?
                <div className="sameline" style={{ padding: 10, borderWidth: 1, borderStyle: 'solid', textAlign: 'center' }}>

                    Current Vendor:
                    <Dropdown width="150" onSelect={(e) => setVendorSelected(e)}>
                        <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                            {vendorSelected}
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            <Dropdown.Item as='daily' eventKey={"12Data"} value={"12Data"}>12Data</Dropdown.Item>
                            <Dropdown.Item as='weekly' eventKey={"schwab"} value={"schwab"}>Schwab</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>

                    <TwoStepConfirmationButton id={vendorSelected} text="Change" onConfirmed={handleVendorChange} />

                    <br></br>
                    <br></br>
                    Quick Quote: <input type="text" onChange={(e) => setQuickQuoteSymbol(e.target.value)}></input>&nbsp;<button className="btn btn-secondary" onClick={(e) => handleLoadQuickQuote()}>Get Quote</button>
                    {quickQuote ? <pre>{JSON.stringify(quickQuote, null, '\t')}</pre> : null}

                    <br></br>
                    <br></br>

                    <TwoStepConfirmationButton id={0} text="Update Quote Cache" onConfirmed={handleUpdateQuoteCache} />
                </div>
                : null}

            <br></br>
            <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                <span className="text-muted small">
                    Tracking <strong>{historicalData.length}</strong> symbols in historical quotes database.
                </span>
                {spyStartDate && (
                    <span className="badge bg-light text-dark border font-monospace px-2 py-1">
                        Benchmark Baseline (SPY Start Date): <strong className="text-primary">{spyStartDate}</strong>
                    </span>
                )}
            </div>
            <RichTable data={getHistoricalData()} className="table-striped table-hover table-condensed align-middle" mappers={[
                { title: 'Status', field: 'status', formatter: statusFormatter },
                { title: 'Symbol', field: 'symbol', formatter: symbolFormatter },
                { title: 'Start Date', field: 'startDate', formatter: dateFormatter },
                { title: 'Last Date', field: 'lastDate', formatter: dateFormatter },
                { title: 'Historical Bars', field: 'count', formatter: historicalCountFormatter },
                { title: 'Actions', field: 'symbol', formatter: reloadDataFormatter },
            ]} />

            <ActionResult result={result} />

            {/* ENHANCED INTELLIGENT QUOTE AUDIT DRAWER */}
            <Drawer
                anchor="left"
                open={showAuditDrawer}
                onClose={() => setShowAuditDrawer(false)}
            >
                <div style={{ padding: '24px', width: 'min(920px, 95vw)', height: '100%', overflowY: 'auto' }}>

                    {/* DRAWER HEADER */}
                    <div className="d-flex justify-content-between align-items-start border-bottom pb-3 mb-3">
                        <div>
                            <h3 className="mb-1 d-flex align-items-center gap-2">
                                <span>Quote Audit: {auditSymbol}</span>
                                {auditLoading && <Spinner animation="border" size="sm" className="ms-2" />}
                            </h3>
                            <div className="text-muted small">
                                Benchmark: <Badge bg="primary">{auditResult?.benchmark || (auditSymbol === 'SPY' ? 'QQQ' : 'SPY')}</Badge>
                                &nbsp;&bull;&nbsp;
                                Evaluates dates strictly starting from {auditSymbol}'s first trading record.
                            </div>
                        </div>
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => loadAudit(auditSymbol)}
                                disabled={auditLoading}
                            >
                                🔄 Refresh
                            </Button>
                            <Button variant="secondary" size="sm" onClick={() => setShowAuditDrawer(false)}>
                                Close
                            </Button>
                        </div>
                    </div>

                    {/* ACTION NOTICE ALERT */}
                    {auditNotice && (
                        <Alert
                            variant={auditNotice.type}
                            dismissible
                            onClose={() => setAuditNotice(null)}
                            className="py-2"
                        >
                            {auditNotice.text}
                        </Alert>
                    )}

                    {/* METRIC SUMMARY CARDS */}
                    {auditResult && (
                        <div className="row g-2 mb-3">
                            <div className="col-6 col-md">
                                <div className="border rounded p-2 text-center bg-light h-100">
                                    <div className="text-muted small">{auditSymbol} Start</div>
                                    <div className="fw-bold text-dark font-monospace">{auditResult.startDate || 'No Data'}</div>
                                </div>
                            </div>
                            <div className="col-6 col-md">
                                <div className="border rounded p-2 text-center bg-light h-100 border-primary">
                                    <div className="text-muted small">{auditResult.benchmark} (Bench) Start</div>
                                    <div className="fw-bold text-primary font-monospace">{auditResult.benchmarkStartDate || 'No Data'}</div>
                                </div>
                            </div>
                            <div className="col-6 col-md">
                                <div className="border rounded p-2 text-center bg-light h-100">
                                    <div className="text-muted small">History End</div>
                                    <div className="fw-bold text-dark font-monospace">{auditResult.endDate || 'No Data'}</div>
                                </div>
                            </div>
                            <div className="col-6 col-md">
                                <div className="border rounded p-2 text-center bg-light h-100">
                                    <div className="text-muted small">Missing Dates</div>
                                    <div>
                                        <Badge bg={auditResult.missingDatesCount > 0 ? 'warning' : 'success'} className="fs-6 text-dark">
                                            {auditResult.missingDatesCount || 0} missing
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                            <div className="col-6 col-md">
                                <div className="border rounded p-2 text-center bg-light h-100">
                                    <div className="text-muted small">Duplicate Days</div>
                                    <div>
                                        <Badge bg={auditResult.duplicateDaysCount > 0 ? 'danger' : 'success'} className="fs-6">
                                            {auditResult.duplicateDaysCount || 0} days
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                            <div className="col-6 col-md">
                                <div className="border rounded p-2 text-center bg-light h-100">
                                    <div className="text-muted small">Excess Dates (Since {auditResult.benchmark})</div>
                                    <div>
                                        <Badge bg={auditResult.excessCount > 0 ? 'danger' : 'success'} className="fs-6">
                                            {auditResult.excessCount || 0} excess
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* AUDIT DETAILS TABS */}
                    {auditResult ? (
                        <Tabs
                            activeKey={auditTab}
                            onSelect={(k) => setAuditTab(k)}
                            className="mb-3"
                        >
                            {/* TAB 1: MISSING DATES */}
                            <Tab
                                eventKey="missing"
                                title={
                                    <span>
                                        Missing Dates{' '}
                                        <Badge bg={auditResult.missingDatesCount > 0 ? 'warning' : 'secondary'} className="text-dark">
                                            {auditResult.missingDatesCount || 0}
                                        </Badge>
                                    </span>
                                }
                            >
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div className="text-muted small">
                                        Dates present in <strong>{auditResult.benchmark}</strong> on/after {auditResult.startDate}, but missing for <strong>{auditSymbol}</strong>:
                                    </div>
                                    <Button
                                        variant="success"
                                        size="sm"
                                        onClick={() => handleOpenFillModal('', auditSymbol)}
                                    >
                                        + Manual Entry
                                    </Button>
                                </div>

                                {auditResult.missingDates && auditResult.missingDates.length > 0 ? (
                                    <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
                                        <Table striped hover bordered size="sm" className="align-middle">
                                            <thead className="table-dark sticky-top">
                                                <tr>
                                                    <th style={{ width: '50px' }}>#</th>
                                                    <th>Missing Date</th>
                                                    <th>Benchmark ({auditResult.benchmark})</th>
                                                    <th>Status</th>
                                                    <th style={{ width: '130px', textAlign: 'center' }}>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {auditResult.missingDates.map((dateStr, idx) => (
                                                    <tr key={dateStr}>
                                                        <td>{idx + 1}</td>
                                                        <td className="font-monospace fw-bold">{dateStr}</td>
                                                        <td><Badge bg="primary">Has Data</Badge></td>
                                                        <td><span className="text-danger">● Missing Bar</span></td>
                                                        <td className="text-center">
                                                            <Button
                                                                variant="outline-success"
                                                                size="sm"
                                                                onClick={() => handleOpenFillModal(dateStr, auditSymbol)}
                                                            >
                                                                ✍ Fill In
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                ) : (
                                    <Alert variant="success" className="my-3 text-center">
                                        ✓ <strong>Zero Missing Dates!</strong> All trading days match {auditResult.benchmark} since {auditResult.startDate}.
                                    </Alert>
                                )}
                            </Tab>

                            {/* TAB 2: DUPLICATE RECORDS */}
                            <Tab
                                eventKey="duplicates"
                                title={
                                    <span>
                                        Duplicates{' '}
                                        <Badge bg={auditResult.duplicateDaysCount > 0 ? 'danger' : 'secondary'}>
                                            {auditResult.duplicateDaysCount || 0}
                                        </Badge>
                                    </span>
                                }
                            >
                                <div className="text-muted small mb-2">
                                    Calendar dates with multiple quote records. Review prices and timestamps, then delete unwanted duplicates:
                                </div>

                                {auditResult.duplicates && auditResult.duplicates.length > 0 ? (
                                    <div style={{ maxHeight: '480px', overflowY: 'auto' }}>
                                        {auditResult.duplicates.map(dupeGroup => (
                                            <div key={dupeGroup.date} className="border rounded mb-3 p-2 bg-white shadow-sm">
                                                <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                                                    <span className="fw-bold font-monospace fs-6">
                                                        📅 {dupeGroup.date}
                                                    </span>
                                                    <Badge bg="danger">{dupeGroup.count} Records</Badge>
                                                </div>

                                                <Table striped bordered hover size="sm" className="mb-0 align-middle">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>DB ID</th>
                                                            <th>Timestamp</th>
                                                            <th>Open</th>
                                                            <th>High</th>
                                                            <th>Low</th>
                                                            <th>Close</th>
                                                            <th>Volume</th>
                                                            <th style={{ width: '100px', textAlign: 'center' }}>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {dupeGroup.records.map((rec) => (
                                                            <tr key={rec.id}>
                                                                <td className="text-muted small">#{rec.id}</td>
                                                                <td className="font-monospace small">{rec.dateTime}</td>
                                                                <td>{rec.open != null ? rec.open.toFixed(2) : '-'}</td>
                                                                <td>{rec.high != null ? rec.high.toFixed(2) : '-'}</td>
                                                                <td>{rec.low != null ? rec.low.toFixed(2) : '-'}</td>
                                                                <td className="fw-bold">{rec.close != null ? rec.close.toFixed(2) : '-'}</td>
                                                                <td className="small">{rec.volume ? rec.volume.toLocaleString() : '0'}</td>
                                                                <td className="text-center">
                                                                    <Button
                                                                        variant="outline-danger"
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            if (window.confirm(`Delete record #${rec.id} (${rec.dateTime}, Close: $${rec.close})?`)) {
                                                                                handleDeleteDuplicate(rec.id);
                                                                            }
                                                                        }}
                                                                    >
                                                                        🗑 Delete
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <Alert variant="success" className="my-3 text-center">
                                        ✓ <strong>No duplicate records found!</strong> Every trading day has exactly one entry.
                                    </Alert>
                                )}
                            </Tab>

                            {/* TAB 3: EXCESS / NON-MARKET DATES */}
                            <Tab
                                eventKey="excess"
                                title={
                                    <span>
                                        Excess Dates{' '}
                                        <Badge bg={auditResult.excessCount > 0 ? 'danger' : 'secondary'}>
                                            {auditResult.excessCount || 0}
                                        </Badge>
                                    </span>
                                }
                            >
                                <div className="text-muted small mb-2">
                                    Dates where <strong>{auditSymbol}</strong> has a quote record since <strong>{auditResult.benchmark}</strong> started ({auditResult.benchmarkStartDate}), but {auditResult.benchmark} has no trading bar (e.g. weekend trades, market holidays, or data errors):
                                </div>

                                {auditResult.excessRecords && auditResult.excessRecords.length > 0 ? (
                                    <div style={{ maxHeight: '480px', overflowY: 'auto' }}>
                                        <Table striped bordered hover size="sm" className="mb-0 align-middle">
                                            <thead className="table-dark sticky-top">
                                                <tr>
                                                    <th>DB ID</th>
                                                    <th>Date</th>
                                                    <th>Day of Week</th>
                                                    <th>Timestamp</th>
                                                    <th>Open</th>
                                                    <th>High</th>
                                                    <th>Low</th>
                                                    <th>Close</th>
                                                    <th>Volume</th>
                                                    <th style={{ width: '100px', textAlign: 'center' }}>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {auditResult.excessRecords.map((rec) => (
                                                    <tr key={rec.id}>
                                                        <td className="text-muted small">#{rec.id}</td>
                                                        <td className="font-monospace fw-bold">{rec.calDate}</td>
                                                        <td>
                                                            <Badge bg={rec.dayOfWeek === 'Saturday' || rec.dayOfWeek === 'Sunday' ? 'warning' : 'info'} className="text-dark">
                                                                {rec.dayOfWeek}
                                                            </Badge>
                                                        </td>
                                                        <td className="font-monospace small text-muted">{rec.dateTime}</td>
                                                        <td>{rec.open != null ? rec.open.toFixed(2) : '-'}</td>
                                                        <td>{rec.high != null ? rec.high.toFixed(2) : '-'}</td>
                                                        <td>{rec.low != null ? rec.low.toFixed(2) : '-'}</td>
                                                        <td className="fw-bold">{rec.close != null ? rec.close.toFixed(2) : '-'}</td>
                                                        <td className="small">{rec.volume ? rec.volume.toLocaleString() : '0'}</td>
                                                        <td className="text-center">
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => {
                                                                    if (window.confirm(`Delete excess record #${rec.id} (${rec.calDate} ${rec.dayOfWeek}, Close: $${rec.close})?`)) {
                                                                        handleDeleteDuplicate(rec.id);
                                                                    }
                                                                }}
                                                            >
                                                                🗑 Delete
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                ) : (
                                    <Alert variant="success" className="my-3 text-center">
                                        ✓ <strong>No excess records found!</strong> Every date aligns with a valid {auditResult.benchmark} market day.
                                    </Alert>
                                )}
                            </Tab>

                            {/* TAB 4: YEARLY COMPARISON */}
                            <Tab eventKey="yearly" title="Yearly Breakdown">
                                <div className="text-muted small mb-2">
                                    Annual bar counts compared to benchmark ({auditResult.benchmark}) since {auditResult.startDate}:
                                </div>
                                <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
                                    <Table striped hover bordered size="sm" className="text-center align-middle">
                                        <thead className="table-dark">
                                            <tr>
                                                <th>Year</th>
                                                <th>{auditSymbol} Bars</th>
                                                <th>{auditResult.benchmark} Bars</th>
                                                <th>Difference</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {auditResult.yearlyStats && auditResult.yearlyStats.map(y => {
                                                const isMatch = y.difference === 0;
                                                const isDeficit = y.difference < 0;
                                                return (
                                                    <tr key={y.year}>
                                                        <td className="fw-bold">{y.year}</td>
                                                        <td>{y.symbolCount}</td>
                                                        <td>{y.benchmarkCount}</td>
                                                        <td className="fw-bold font-monospace">
                                                            {y.difference > 0 ? `+${y.difference}` : y.difference}
                                                        </td>
                                                        <td>
                                                            {y.isPreBenchmark ? (
                                                                <Badge bg="info" className="text-dark">Pre-{auditResult.benchmark}</Badge>
                                                            ) : isMatch ? (
                                                                <Badge bg="success">Match</Badge>
                                                            ) : isDeficit ? (
                                                                <Badge bg="warning" className="text-dark">{Math.abs(y.difference)} Missing</Badge>
                                                            ) : (
                                                                <Badge bg="danger">+{y.difference} Excess</Badge>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            </Tab>
                        </Tabs>
                    ) : (
                        <div className="text-center py-5">
                            {auditLoading ? (
                                <>
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-2 text-muted">Auditing quote history against {auditSymbol === 'SPY' ? 'QQQ' : 'SPY'}...</p>
                                </>
                            ) : (
                                <p className="text-muted">No audit data available for {auditSymbol}.</p>
                            )}
                        </div>
                    )}
                </div>
            </Drawer>

            {/* FILL IN / ADD MANUAL QUOTE MODAL */}
            <Modal show={showFillModal} onHide={() => setShowFillModal(false)} centered>
                <Form onSubmit={handleSaveFillQuote}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            Fill In Quote: <span className="text-primary">{fillForm.symbol || auditSymbol}</span>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p className="text-muted small mb-3">
                            Manually enter missing daily bar data for <strong>{fillForm.symbol || auditSymbol}</strong>. Close price is required; Open, High, and Low will default to Close if left blank.
                        </p>

                        <div className="row g-2 mb-2">
                            <div className="col-4">
                                <Form.Group>
                                    <Form.Label className="fw-bold small mb-1">Symbol*</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={fillForm.symbol}
                                        onChange={(e) => setFillForm({ ...fillForm, symbol: e.target.value.toUpperCase() })}
                                        placeholder="e.g. SMH"
                                        required
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-8">
                                <Form.Group>
                                    <Form.Label className="fw-bold small mb-1">Trading Date (YYYY-MM-DD or M/D/YYYY)*</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={fillForm.date}
                                        onChange={(e) => setFillForm({ ...fillForm, date: e.target.value })}
                                        placeholder="YYYY-MM-DD or M/D/YYYY"
                                        required
                                    />
                                </Form.Group>
                            </div>
                        </div>

                        <div className="row g-2 mb-2">
                            <div className="col-6">
                                <Form.Group>
                                    <Form.Label className="fw-bold small mb-1">Close Price ($)*</Form.Label>
                                    <InputGroup size="sm">
                                        <InputGroup.Text>$</InputGroup.Text>
                                        <Form.Control
                                            type="number"
                                            step="0.0001"
                                            value={fillForm.close}
                                            onChange={(e) => setFillForm({ ...fillForm, close: e.target.value })}
                                            placeholder="e.g. 150.25"
                                            autoFocus
                                            required
                                        />
                                    </InputGroup>
                                </Form.Group>
                            </div>
                            <div className="col-6">
                                <Form.Group>
                                    <Form.Label className="fw-bold small mb-1">Volume</Form.Label>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        value={fillForm.volume}
                                        onChange={(e) => setFillForm({ ...fillForm, volume: e.target.value })}
                                        placeholder="e.g. 1000000"
                                    />
                                </Form.Group>
                            </div>
                        </div>

                        <div className="row g-2 mb-2">
                            <div className="col-4">
                                <Form.Group>
                                    <Form.Label className="small text-muted mb-1">Open ($)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.0001"
                                        value={fillForm.open}
                                        onChange={(e) => setFillForm({ ...fillForm, open: e.target.value })}
                                        placeholder="Optional"
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-4">
                                <Form.Group>
                                    <Form.Label className="small text-muted mb-1">High ($)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.0001"
                                        value={fillForm.high}
                                        onChange={(e) => setFillForm({ ...fillForm, high: e.target.value })}
                                        placeholder="Optional"
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-4">
                                <Form.Group>
                                    <Form.Label className="small text-muted mb-1">Low ($)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.0001"
                                        value={fillForm.low}
                                        onChange={(e) => setFillForm({ ...fillForm, low: e.target.value })}
                                        placeholder="Optional"
                                    />
                                </Form.Group>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowFillModal(false)} disabled={fillLoading}>
                            Cancel
                        </Button>
                        <Button variant="success" type="submit" disabled={fillLoading}>
                            {fillLoading ? <Spinner animation="border" size="sm" /> : 'Save Quote Record'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

        </div>
    );
}

export default MarketDataStatus;
